import { useCallback, useMemo, useRef, useState } from 'react'
import {
  FileText,
  Image as ImageIcon,
  Download,
  Eye,
  Pencil,
  Printer,
  Trash2,
  Upload,
  Loader2,
  MoreHorizontal,
  Check,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { DocumentViewerDialog } from '@/components/features/project-documents/DocumentViewerDialog'
import { DocumentBundleDialog } from '@/components/features/project-documents/DocumentBundleDialog'
import { showSuccessToast, showErrorToast } from '@/lib/toast'
import { formatDate, formatFileSize } from '@/lib/format'
import { useProjectDocuments } from '@/hooks/useProjectDocuments'
import { useDocumentBlobUrls } from '@/hooks/useDocumentBlobUrls'
import {
  DOCUMENT_SORT_LABELS,
  archiveFileName,
  documentDownloadName,
  selectionInDisplayOrder,
  sortDocuments,
  type DocumentSort,
} from '@/lib/projectDocuments'
import { saveObjectUrl } from '@/lib/printBlob'
import {
  uploadProjectDocument,
  renameProjectDocument,
  deleteProjectDocument,
  downloadProjectDocumentArchive,
  type ProjectDocumentDto,
} from '@/lib/api/v2/projects'

function getUploaderInitials(firstName?: string | null, lastName?: string | null): string {
  return [firstName, lastName]
    .filter((n): n is string => !!n?.trim())
    .map((n) => n.trim()[0].toUpperCase() + '.')
    .join('') || '—'
}

interface ProjectDocumentsSectionProps {
  projectId: string | undefined
  /** Used to name the printable dossier file. */
  projectName?: string | null
}

/**
 * SOLY-DOC-001 V1 — execution (chantier) documents of a project: list, upload (image/PDF), rename,
 * soft-delete. Internal only; access is enforced server-side.
 *
 * SOLY-DOC-002 — working through many files at once: click a name to open the in-app viewer
 * (←/→, "12 / 30", files ticked "vu" for the session); "Tout imprimer" or a ticked selection builds
 * ONE PDF with a cover sheet, in the order shown — one print dialog for the whole physical folder
 * instead of one per file.
 */
export function ProjectDocumentsSection({ projectId, projectName }: ProjectDocumentsSectionProps) {
  const { documents, isLoading, error, refetch } = useProjectDocuments(projectId)
  const { getUrl } = useDocumentBlobUrls(projectId)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [renameTarget, setRenameTarget] = useState<ProjectDocumentDto | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const [isRenaming, setIsRenaming] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<ProjectDocumentDto | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [sort, setSort] = useState<DocumentSort>('newest')
  const [selected, setSelected] = useState<Set<string>>(() => new Set())
  const [seen, setSeen] = useState<Set<string>>(() => new Set())
  // The viewer follows a document, not a position: a refetch (upload by a colleague) must not
  // silently swap the file on screen.
  const [viewerDocId, setViewerDocId] = useState<string | null>(null)
  const [bundleIds, setBundleIds] = useState<string[] | null>(null)
  const [isZipping, setIsZipping] = useState(false)
  const zippingRef = useRef(false)

  const displayed = useMemo(() => sortDocuments(documents, sort), [documents, sort])
  // Ignore ids of documents that are gone (deleted, refetched).
  const liveSelected = useMemo(() => {
    const ids = new Set(documents.map((d) => d.id))
    return new Set([...selected].filter((id) => ids.has(id)))
  }, [documents, selected])
  const allSelected = displayed.length > 0 && liveSelected.size === displayed.length
  const someSelected = liveSelected.size > 0 && !allSelected
  const seenCount = displayed.filter((d) => seen.has(d.id)).length
  const viewerPosition = viewerDocId ? displayed.findIndex((d) => d.id === viewerDocId) : -1
  const setViewerIndex = useCallback(
    (i: number | null) => setViewerDocId(i === null ? null : (displayed[i]?.id ?? null)),
    [displayed]
  )

  const markSeen = useCallback((id: string) => {
    setSeen((prev) => (prev.has(id) ? prev : new Set(prev).add(id)))
  }, [])

  if (!projectId) return null

  const toggle = (id: string, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (checked) next.add(id)
      else next.delete(id)
      return next
    })
  }

  const printDocuments = (ids: string[]) => {
    if (ids.length > 0) setBundleIds(ids)
  }

  const openViewer = (doc: ProjectDocumentDto) => setViewerDocId(doc.id)

  /** The original files in one ZIP, numbered in table order, to keep in a local folder. */
  const downloadDocuments = async (ids: string[]) => {
    if (ids.length === 0 || zippingRef.current) return
    zippingRef.current = true
    setIsZipping(true)
    try {
      const { zip, missingCount } = await downloadProjectDocumentArchive(projectId, ids)
      const url = URL.createObjectURL(zip)
      saveObjectUrl(url, archiveFileName(projectName))
      // The click has handed the blob to the browser's download; free it once that has started.
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
      if (missingCount > 0) {
        showErrorToast(
          missingCount === 1
            ? "1 document n'a pas pu être téléchargé (voir « Fichiers non inclus.txt » dans le ZIP)"
            : `${missingCount} documents n'ont pas pu être téléchargés (voir « Fichiers non inclus.txt » dans le ZIP)`
        )
      }
    } catch (e: unknown) {
      showErrorToast(e instanceof Error && e.message ? e.message : 'Impossible de télécharger les documents')
    } finally {
      zippingRef.current = false
      setIsZipping(false)
    }
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.currentTarget.files ?? [])
    if (files.length === 0) return
    setIsUploading(true)
    const results = await Promise.allSettled(files.map((f) => uploadProjectDocument(projectId, f)))
    const succeeded = results.filter((r) => r.status === 'fulfilled').length
    const failed = results.filter((r) => r.status === 'rejected').length
    if (succeeded > 0) {
      showSuccessToast(succeeded === 1 ? 'Document ajouté' : `${succeeded} documents ajoutés`)
      await refetch()
    }
    if (failed > 0) {
      showErrorToast(failed === 1 ? "Erreur lors de l'envoi d'un document" : `${failed} documents non envoyés`)
    }
    setIsUploading(false)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleDownload = async (doc: ProjectDocumentDto) => {
    try {
      saveObjectUrl(await getUrl(doc.id), documentDownloadName(doc))
    } catch {
      showErrorToast('Impossible de télécharger le document')
    }
  }

  const submitRename = async () => {
    if (!renameTarget) return
    const name = renameValue.trim()
    if (!name) return
    setIsRenaming(true)
    try {
      await renameProjectDocument(projectId, renameTarget.id, name)
      showSuccessToast('Document renommé')
      setRenameTarget(null)
      await refetch()
    } catch {
      showErrorToast('Erreur lors du renommage')
    } finally {
      setIsRenaming(false)
    }
  }

  const confirmDelete = async () => {
    if (!deleteTarget) return
    setIsDeleting(true)
    try {
      await deleteProjectDocument(projectId, deleteTarget.id)
      showSuccessToast('Document supprimé')
      setDeleteTarget(null)
      await refetch()
    } catch {
      showErrorToast('Erreur lors de la suppression')
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <Card className="py-3">
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <CardTitle className="text-base">Documents du chantier</CardTitle>
            <p className="text-xs text-muted-foreground">
              Plans, photos et documents techniques. Cliquez sur un nom pour les parcourir un par un.
              {seenCount > 0 && (
                <span className="ml-1 font-medium text-foreground" data-testid="project-document-seen-count">
                  · {seenCount} / {displayed.length} vu{seenCount > 1 ? 's' : ''}
                </span>
              )}
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            {documents.length > 1 && (
              <Select value={sort} onValueChange={(v) => setSort(v as DocumentSort)}>
                <SelectTrigger className="h-8 w-[190px] text-xs" data-testid="project-document-sort-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(DOCUMENT_SORT_LABELS) as DocumentSort[]).map((key) => (
                    <SelectItem key={key} value={key}>
                      {DOCUMENT_SORT_LABELS[key]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {documents.length > 0 && (
              <Button
                size="sm"
                variant="outline"
                disabled={isZipping}
                onClick={() => printDocuments(displayed.map((d) => d.id))}
                data-testid="project-document-print-all-btn"
              >
                <Printer className="h-4 w-4" />
                Tout imprimer
              </Button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,application/pdf"
              multiple
              className="hidden"
              onChange={handleFileChange}
              data-testid="project-document-file-input"
            />
            <Button
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              data-testid="project-document-upload-btn"
            >
              {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {isUploading ? 'Envoi…' : 'Ajouter'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Floating: with 30 rows the box you tick is far below the card header. */}
        {liveSelected.size > 0 && (
          <div
            className="fixed bottom-4 left-1/2 z-40 flex w-[min(36rem,calc(100vw-2rem))] -translate-x-1/2 flex-wrap items-center gap-2 rounded-xl border bg-background px-4 py-2 text-sm shadow-lg"
            role="region"
            aria-label="Sélection de documents"
            data-testid="project-document-bulk-bar"
          >
            <span className="font-medium">
              {liveSelected.size} sélectionné{liveSelected.size > 1 ? 's' : ''}
            </span>
            <Button
              size="sm"
              className="h-7"
              // Both share the server's single merge slot: one at a time.
              disabled={isZipping}
              onClick={() => printDocuments(selectionInDisplayOrder(displayed, liveSelected))}
              data-testid="project-document-print-selection-btn"
            >
              <Printer className="h-4 w-4" />
              Imprimer ({liveSelected.size})
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-7"
              disabled={isZipping}
              onClick={() => downloadDocuments(selectionInDisplayOrder(displayed, liveSelected))}
              data-testid="project-document-download-selection-btn"
            >
              {isZipping ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Télécharger ({liveSelected.size})
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="ml-auto h-7"
              onClick={() => setSelected(new Set())}
              data-testid="project-document-clear-selection-btn"
            >
              Désélectionner
            </Button>
          </div>
        )}
        <div className="rounded-lg border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10 py-1.5">
                  <Checkbox
                    checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                    onCheckedChange={(c) => setSelected(c === true ? new Set(displayed.map((d) => d.id)) : new Set())}
                    disabled={displayed.length === 0}
                    aria-label="Tout sélectionner"
                    data-testid="project-document-select-all"
                  />
                </TableHead>
                <TableHead className="py-1.5">Document</TableHead>
                <TableHead className="py-1.5">Taille</TableHead>
                <TableHead className="py-1.5">Par</TableHead>
                <TableHead className="py-1.5">Ajouté le</TableHead>
                <TableHead className="py-1.5 text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {error ? (
                <TableRow>
                  <TableCell colSpan={6} className="py-6 text-center text-sm text-destructive">
                    Impossible de charger les documents.
                  </TableCell>
                </TableRow>
              ) : isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="py-6 text-center text-sm text-muted-foreground">
                    Chargement…
                  </TableCell>
                </TableRow>
              ) : documents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="py-6 text-center text-sm text-muted-foreground">
                    Aucun document — ajoutez le premier plan ou la première photo.
                  </TableCell>
                </TableRow>
              ) : (
                displayed.map((doc) => (
                  <TableRow
                    key={doc.id}
                    data-state={liveSelected.has(doc.id) ? 'selected' : undefined}
                    data-testid="project-document-row"
                  >
                    <TableCell className="py-1.5">
                      <Checkbox
                        checked={liveSelected.has(doc.id)}
                        onCheckedChange={(c) => toggle(doc.id, c === true)}
                        aria-label={`Sélectionner ${doc.displayName}`}
                        data-testid="project-document-select"
                      />
                    </TableCell>
                    <TableCell className="max-w-0 py-1.5 font-medium">
                      <button
                        type="button"
                        onClick={() => openViewer(doc)}
                        className="flex max-w-full items-center gap-2 rounded text-left hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                        title="Afficher"
                        data-testid="project-document-open-btn"
                      >
                        {doc.contentType.startsWith('image/') ? (
                          <ImageIcon className="h-4 w-4 shrink-0 text-slate-500" />
                        ) : (
                          <FileText className="h-4 w-4 shrink-0 text-red-500" />
                        )}
                        <span className="truncate">{doc.displayName}</span>
                        {seen.has(doc.id) && (
                          <span
                            className="inline-flex shrink-0 items-center gap-0.5 text-xs font-normal text-emerald-700"
                            title="Consulté pendant cette session"
                            data-testid="project-document-seen-badge"
                          >
                            <Check className="h-3.5 w-3.5" />
                            vu
                          </span>
                        )}
                      </button>
                    </TableCell>
                    <TableCell className="py-1.5 text-sm text-muted-foreground">{formatFileSize(doc.size)}</TableCell>
                    <TableCell className="py-1.5 text-sm text-muted-foreground">
                      {getUploaderInitials(doc.uploaderFirstName, doc.uploaderLastName)}
                    </TableCell>
                    <TableCell className="py-1.5 text-sm text-muted-foreground">
                      {formatDate(doc.createdAt, 'd MMM yyyy')}
                    </TableCell>
                    <TableCell className="py-1.5 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            data-testid="project-document-actions-btn"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openViewer(doc)} data-testid="project-document-view-item">
                            <Eye className="h-4 w-4 mr-2" />
                            Afficher
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleDownload(doc)}
                            data-testid="project-document-download-item"
                          >
                            <Download className="h-4 w-4 mr-2" />
                            Télécharger
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => {
                              setRenameTarget(doc)
                              setRenameValue(doc.displayName)
                            }}
                            data-testid="project-document-rename-item"
                          >
                            <Pencil className="h-4 w-4 mr-2" />
                            Renommer
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => setDeleteTarget(doc)}
                            className="text-destructive"
                            data-testid="project-document-delete-item"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Supprimer
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>

      <DocumentViewerDialog
        documents={displayed}
        index={viewerPosition >= 0 ? viewerPosition : null}
        onIndexChange={setViewerIndex}
        getUrl={getUrl}
        seenIds={seen}
        onSeen={markSeen}
      />

      <DocumentBundleDialog
        key={bundleIds?.join(',') ?? 'closed'}
        projectId={projectId}
        projectName={projectName}
        documentIds={bundleIds}
        orderLabel={DOCUMENT_SORT_LABELS[sort]}
        onClose={() => setBundleIds(null)}
      />

      <Dialog
        open={renameTarget !== null}
        onOpenChange={(open) => {
          if (!open) setRenameTarget(null)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Renommer le document</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="project-document-rename-input">Nom</Label>
            <Input
              id="project-document-rename-input"
              value={renameValue}
              onChange={(e) => setRenameValue(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void submitRename()
              }}
              data-testid="project-document-rename-input"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRenameTarget(null)}
              data-testid="project-document-rename-cancel-btn"
            >
              Annuler
            </Button>
            <Button
              onClick={submitRename}
              disabled={isRenaming || renameValue.trim().length === 0}
              data-testid="project-document-rename-save-btn"
            >
              {isRenaming ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce document ?</AlertDialogTitle>
            <AlertDialogDescription>
              «&nbsp;{deleteTarget?.displayName}&nbsp;» sera retiré du chantier.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="project-document-delete-cancel-btn">Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={isDeleting}
              data-testid="project-document-delete-confirm-btn"
            >
              {isDeleting ? 'Suppression…' : 'Supprimer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  )
}
